import * as React from "react";
import { Container, Box, Typography, Divider, List, ListItem, ListItemText } from "@mui/material";
import { useTranslation } from "react-i18next";

export default function PrivacyPage() {
  const { t } = useTranslation();

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, sm: 6 } }}>
      <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: -0.5 }}>
        {t("legal.privacy.title")}
      </Typography>
      <Typography sx={{ color: "text.secondary", mt: 1 }}>
        {t("legal.privacy.lastUpdated")}
      </Typography>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography>{t("legal.privacy.intro.p1")}</Typography>
        <Typography>{t("legal.privacy.intro.p2")}</Typography>

        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t("legal.privacy.sections.dataCollected.title")}
        </Typography>
        <Typography>{t("legal.privacy.sections.dataCollected.intro")}</Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {t("legal.privacy.sections.dataCollected.direct.title")}
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.dataCollected.direct.bullets.fullName")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.dataCollected.direct.bullets.email")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.dataCollected.direct.bullets.accountInfo")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.dataCollected.direct.bullets.formInfo")} />
          </ListItem>
        </List>

        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {t("legal.privacy.sections.dataCollected.auto.title")}
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.dataCollected.auto.bullets.ip")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.dataCollected.auto.bullets.device")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.dataCollected.auto.bullets.analytics")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.dataCollected.auto.bullets.cookies")} />
          </ListItem>
        </List>

        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t("legal.privacy.sections.dataUsage.title")}
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.dataUsage.bullets.improveServices")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.dataUsage.bullets.manageAccounts")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.dataUsage.bullets.security")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.dataUsage.bullets.respond")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.dataUsage.bullets.comms")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.dataUsage.bullets.stats")} />
          </ListItem>
        </List>

        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t("legal.privacy.sections.sharing.title")}
        </Typography>
        <Typography>{t("legal.privacy.sections.sharing.p1")}</Typography>
        <Typography>{t("legal.privacy.sections.sharing.p2")}</Typography>
        <List dense>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.sharing.bullets.providers")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.sharing.bullets.authorities")} />
          </ListItem>
        </List>
        <Typography>{t("legal.privacy.sections.sharing.p3")}</Typography>

        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t("legal.privacy.sections.cookies.title")}
        </Typography>
        <Typography>{t("legal.privacy.sections.cookies.intro")}</Typography>
        <List dense>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.cookies.bullets.functionality")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.cookies.bullets.ux")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.cookies.bullets.audience")} />
          </ListItem>
        </List>
        <Typography>{t("legal.privacy.sections.cookies.outro")}</Typography>

        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t("legal.privacy.sections.retention.title")}
        </Typography>
        <Typography>{t("legal.privacy.sections.retention.body")}</Typography>

        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t("legal.privacy.sections.security.title")}
        </Typography>
        <Typography>{t("legal.privacy.sections.security.intro")}</Typography>
        <List dense>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.security.bullets.unauthorized")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.security.bullets.loss")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.security.bullets.disclosure")} />
          </ListItem>
        </List>

        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t("legal.privacy.sections.rights.title")}
        </Typography>
        <Typography>{t("legal.privacy.sections.rights.intro")}</Typography>
        <List dense>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.rights.bullets.access")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.rights.bullets.rectification")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.rights.bullets.deletion")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.rights.bullets.restriction")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.rights.bullets.objection")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("legal.privacy.sections.rights.bullets.portability")} />
          </ListItem>
        </List>
        <Typography>{t("legal.privacy.sections.rights.contact", { email: "privacy@your-domain.com" })}</Typography>

        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t("legal.privacy.sections.minors.title")}
        </Typography>
        <Typography>{t("legal.privacy.sections.minors.body")}</Typography>

        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t("legal.privacy.sections.changes.title")}
        </Typography>
        <Typography>{t("legal.privacy.sections.changes.body")}</Typography>

        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t("legal.privacy.sections.contact.title")}
        </Typography>
        <Typography>{t("legal.privacy.sections.contact.body", { email: "privacy@your-domain.com" })}</Typography>

        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t("legal.privacy.sections.controller.title")}
        </Typography>
        <Typography>{t("legal.privacy.sections.controller.body")}</Typography>

        <Divider sx={{ my: 2 }} />
        <Typography>{t("legal.privacy.footer")}</Typography>
      </Box>
    </Container>
  );
}

